import type { SiteId } from '@/lib/site'

/**
 * Per-site institution terminology, the sibling of the market spelling layer
 * (spelling.ts). Learner copy is written against a generic US "bank"; sponsor
 * sites whose audience banks elsewhere get the word swapped at render time so
 * one content source serves every tenant:
 *
 *   - dcuc (Defense Credit Union Council): bank → credit union
 *   - amba (military *banks*): source copy is already right — no swap
 *
 * Applied by useTerminology (a DOM text-node walker mounted in __root) rather
 * than per-string call sites, so it covers slide copy files, inline TSX copy,
 * chapters.ts, and scams/index.ts without touching content source.
 */

type TermPair = {
  from: string
  to: string
}

/**
 * Phrases kept as-is even though they contain a swapped word. "Bank transfer"
 * is the name of the payment rail (credit unions send them too); "credit
 * union transfer" is not a thing.
 */
const PROTECTED_PHRASES = ['bank transfer', 'bank transfers']

const SITE_TERMS: Partial<Record<SiteId, TermPair[]>> = {
  dcuc: [
    { from: 'banks', to: 'credit unions' },
    { from: 'bank', to: 'credit union' },
  ],
  // The GranGuard face is distributed by credit unions to their members, so
  // course examples say "your credit union", not "your bank" (CU audiences
  // are pointed about the difference). Same pass as DCUC; "bank transfer"
  // stays protected as the name of the payment rail.
  granguard: [
    { from: 'banks', to: 'credit unions' },
    { from: 'bank', to: 'credit union' },
  ],
}

const matcherCache = new Map<SiteId, RegExp>()

function matcherFor(site: SiteId, pairs: TermPair[]): RegExp {
  let re = matcherCache.get(site)
  if (!re) {
    // Protected phrases first so they win over the bare word, longest pair
    // first so "banks" isn't half-eaten by "bank".
    const alternatives = [
      ...PROTECTED_PHRASES,
      ...pairs.map(p => p.from),
    ].map(a => `\\b${a}\\b`)
    re = new RegExp(alternatives.join('|'), 'gi')
    matcherCache.set(site, re)
  }
  return re
}

const titleCaseWords = (s: string) =>
  s.replace(/\b[a-z]/g, c => c.toUpperCase())

/**
 * "bank" → "credit union", "BANK" → "CREDIT UNION". Capitalized sources split
 * on number (per the copy audit): singular "Bank" only occurs in label/UI
 * contexts ("Your Bank", "Recipient's Bank") → "Credit Union", while plural
 * "Banks" only occurs at sentence starts ("Banks, police, and ...") →
 * "Credit unions".
 */
function preserveCase(source: string, replacement: string): string {
  if (source.toUpperCase() === source) return replacement.toUpperCase()
  if (source[0] === source[0].toUpperCase()) {
    const capitalized = replacement.charAt(0).toUpperCase() + replacement.slice(1)
    return source.toLowerCase().endsWith('s') ? capitalized : titleCaseWords(capitalized)
  }
  return replacement
}

/** True if the site has any term swaps (lets callers skip work entirely). */
export function siteHasTermSwaps(site: SiteId): boolean {
  return Boolean(SITE_TERMS[site]?.length)
}

/** Swap institution terms in US-source copy for the given site. Idempotent. */
export function localizeTerms(text: string, site: SiteId): string {
  const pairs = SITE_TERMS[site]
  if (!text || !pairs?.length) return text

  return text.replace(matcherFor(site, pairs), match => {
    const lower = match.toLowerCase()
    if (PROTECTED_PHRASES.includes(lower)) return match
    const pair = pairs.find(p => p.from === lower)
    return pair ? preserveCase(match, pair.to) : match
  })
}
